import { StrictMode } from "react";
import { createRoot, hydrateRoot } from "react-dom/client";
import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";

import { MapsProvider } from "./components/Maps";
import { HostEventsProvider } from "./host/events";
import { HostContext, detectHost } from "./host/host";
import { syncHostSession } from "./host/session";
import { installErrorReporting } from "./lib/report";
import { applyTextSize, readTextSize } from "./lib/textSize";
import { applyTheme, readTheme, resolveTheme } from "./lib/theme";
import { entryPath, primeVisitor, shouldHydrate } from "./lib/visitor";
import { createQueryClient } from "./query";
import { routeTree } from "./routeTree.gen";
import "./styles.css";

// The reader's text size (decision 071, F441), applied before the first
// paint so a page never jumps from the standard size to the chosen one.
applyTextSize(readTextSize());
// The appearance (F467, decision 090), the same way: the root marked before the first paint.
applyTheme(readTheme());

// The same application in the browser and inside the companion's shell
// (decision 064): under the shell the pages get the local commands through
// the host and call the platform the shell is set to, with its token.
// The mocked host is for a look in a plain browser (never in the app).
const host = import.meta.env.VITE_MOCK_HOST === "1" ? (await import("./host/mock")).mockHost() : detectHost();
if (host) {
  // The shell's window (its title bar) in the same appearance as the pages; null follows the system by itself.
  const choice = readTheme();
  host.setWindowTheme(choice === "system" ? null : resolveTheme(choice)).catch((e) => console.error("the window's theme could not be set", e));
  // A shell whose state cannot be read (the credential store away) still
  // shows the pages, signed out, with the error in the console.
  await syncHostSession(host).catch((e) => console.error("the shell's session could not be read", e));
}

// The page's own failures reach the admin page (decision 140), after the shell's session is known.
installErrorReporting();

// The shell's window opens on app.html: the address becomes "/" before the router reads it.
const start = entryPath(window.location.pathname, host !== null);
if (start !== null) window.history.replaceState(null, "", start);

const queryClient = createQueryClient();

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: "intent",
  scrollRestoration: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const tree = (
  <StrictMode>
    <HostContext.Provider value={host}>
      <QueryClientProvider client={queryClient}>
        <HostEventsProvider>
          {/* The platform's Google Maps key for the maps (F468); Google's own provider sits inside each map's frame. */}
          <MapsProvider>
            <RouterProvider router={router} />
          </MapsProvider>
        </HostEventsProvider>
      </QueryClientProvider>
    </HostContext.Provider>
  </StrictMode>
);

// A public page prerendered for a visitor (decision 136) is hydrated as it
// stands, from the same answers the prerender had; any other page, and the
// shell's window, start from an empty root.
const root = document.getElementById("root")!;
if (shouldHydrate(root, window.location.pathname, host !== null)) {
  primeVisitor(queryClient);
  // The page's chunks first: an unloaded router renders its pending state,
  // nothing, and the hydration would find the page where it expects none.
  await router.load();
  hydrateRoot(root, tree);
} else {
  if (root.hasChildNodes()) root.replaceChildren();
  createRoot(root).render(tree);
}
